/**
 * Hook decides whether a product card should render the "Tư vấn sản phẩm"
 * (consultation) button instead of "Add to cart".
 *
 * NGUYÊN TẮC (2026-07-19): FE CHỈ đọc `product.eligibility` từ BFF — bao gồm
 * `cantBuy`, `reason`, `message`. MỌI eligibility computation
 * (scope-of-business match, license expiry, BV-1120 MEDICAL_SUPPLY_STORE
 * whitelist, business decisions như EXTERNAL exemption) đều được BFF quyết
 * định (xem frontend-apis-v2/action/eligibility.go computeEligibility +
 * computeBusinessScopeEligibility + skipGateForSKU). FE không duplicate
 * logic client-side.
 *
 * Triggers cho ConsultBtn (chỉ 2):
 *   1) `product.eligibility.cantBuy === true` — BFF definitive verdict.
 *      Fail-open: undefined / null → user thấy Add to cart.
 *   2) `product.isIngredientSpecial === true` — regulatory consult cho
 *      special-ingredient / controlled substances. Đây là ATTRIBUTE của
 *      product (không phải eligibility computed), FE đọc thẳng.
 *
 * Special case — special-ingredient drug + customer in a blocked province
 * (provinceCode in config CONTROLLED_PRODUCT_BLOCKED_PROVINCE_CODES): consult
 * button stays visible but is disabled (see shouldDisableConsultButton). Đây
 * là UI state (disable button), không phải eligibility.
 *
 * ---
 * LEGACY (2026-07-19 — giữ để reference/audit, KHÔNG được gọi từ code active):
 *   - `isConsultMedicine` — client-side scope match (duplicate BFF).
 *   - `isLicenseExpiredForPrescription` — FE-only bridge cho TD-21; đúng ra
 *     BFF nên cover expiry check (task #143).
 *   - `isProductInAllowedCategories` — client-side BV-1120 whitelist check
 *     (duplicate BFF computeBusinessScopeEligibility).
 *   - `isProductBlockedForBusinessLegacy` — client-side MEDICAL_SUPPLY_STORE
 *     gate với whitelist + combo walk (duplicate BFF).
 *   - Helpers: `customerScopeOfBusiness`, `getComboChildren`,
 *     `getComboChildCategoryCodes`, `isComboProduct` — chỉ dùng bởi legacy.
 *
 * Khi task #143 (TD-21) landed + audit chắc chắn không caller nào dùng legacy
 * → xóa toàn bộ block legacy để giảm surface.
 *
 * @returns {{
 *   isProductInAllowedCategories: (product?: Object) => boolean,
 *   isProductBlockedForBusiness: (product?: Object) => boolean,
 *   shouldShowConsultButton: (product?: Object) => boolean,
 *   shouldDisableConsultButton: (product?: Object) => boolean,
 * }}
 */
import { BUSINESS_SCOPE_GATED_SCOPES, ENUM_SCOPE, MEDICAL_SUPPLY_ALLOWED_CATEGORY_CODES } from 'constants/Enums';
import { useAuth } from 'context/Auth';
import { useSetting } from 'context/Settings';
import { useMemo } from 'react';

// App config key holding the province codes where controlled/special-ingredient
// drugs are blocked. Value may be a JSON array or a comma-separated string.
const CONTROLLED_PRODUCT_BLOCKED_PROVINCE_CODES = 'CONTROLLED_PRODUCT_BLOCKED_PROVINCE_CODES';

export function useProductConsultation() {
  const { user } = useAuth();
  const { configs } = useSetting();

  // Extract stable values from user object to avoid unnecessary re-renders
  const gdpInfo = user?.gdpInfo;
  const gppInfo = user?.gppInfo;
  const gspInfo = user?.gspInfo;
  const provinceCode = user?.provinceCode;

  // Province codes where controlled/special-ingredient drugs are blocked,
  // normalized to an array of strings from the app config.
  const blockedProvinceCodes = useMemo(() => {
    const raw = configs?.[CONTROLLED_PRODUCT_BLOCKED_PROVINCE_CODES];
    const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
    return list.map((code) => `${code}`.trim()).filter(Boolean);
  }, [configs]);

  // Whether the customer belongs to a blocked province.
  const isCustomerInBlockedProvince = useMemo(
    () => (provinceCode ? blockedProvinceCodes.includes(`${provinceCode}`) : false),
    [provinceCode, blockedProvinceCodes],
  );

  // Memoize customer scope of business to avoid recalculation
  const customerScopeOfBusiness = useMemo(() => {
    const scopes = [];
    let isLicenseExpired = false;

    // check expiryDate
    const isGdpExpired = gdpInfo?.expiryDate ? new Date(gdpInfo.expiryDate) < new Date() : false;
    const isGppExpired = gppInfo?.expiryDate ? new Date(gppInfo.expiryDate) < new Date() : false;
    const isGspExpired = gspInfo?.expiryDate ? new Date(gspInfo.expiryDate) < new Date() : false;

    if (isGdpExpired || isGppExpired || isGspExpired) {
      isLicenseExpired = true;
    }

    if (gdpInfo?.scopeOfBusiness?.length > 0) {
      scopes.push(...gdpInfo.scopeOfBusiness);
    }

    if (gppInfo?.scopeOfBusiness?.length > 0) {
      scopes.push(...gppInfo.scopeOfBusiness);
    }

    if (gspInfo?.scopeOfBusiness?.length > 0) {
      scopes.push(...gspInfo.scopeOfBusiness);
    }

    // Return unique values
    return {
      scopes: [...new Set(scopes)],
      isLicenseExpired,
    };
  }, [gdpInfo, gppInfo, gspInfo]);

  /**
   * @deprecated LEGACY 2026-07-19 — GIỮ ĐỂ REFERENCE / AUDIT, KHÔNG DÙNG.
   * Function này duplicate BV-1080 scope-match check của BFF
   * (frontend-apis-v2/action/eligibility.go computeEligibility). Doc block đầu
   * file khẳng định "BFF is single source of truth" từ commit ecffa23d4
   * (2026-06-16) nhưng function này chưa được xoá — nên nó cover scope check
   * client-side thứ hai, gây drift với BFF (đặc biệt lộ ra khi business
   * decision 2026-07-19 miễn gate cho SKU EXTERNAL — BFF trả cantBuy=false
   * nhưng FE vẫn block ở đây vì không biết SellerClass).
   *
   * Đã thay bằng isLicenseExpiredForPrescription (chỉ giữ nhánh license
   * expiry — FE-only bridge cho TD-21). Xoá helper này khi audit chắc chắn
   * không còn caller.
   *
   * @param {Object} productData - Product data object
   * @param {string} productData.sellerSubCategoryCode - Product's seller sub category code
   * @returns {boolean} True if product requires consultation, false otherwise
   */
  // eslint-disable-next-line no-unused-vars
  const isConsultMedicine = (productData = {}) => {
    if (!configs || !productData?.sellerSubCategoryCode) return false;

    const customerValidationConfig = configs['CUSTOMER_LICENSE_VALIDATION_CONFIG'];
    if (!customerValidationConfig) return false;

    const requiredScopes = customerValidationConfig[productData.sellerSubCategoryCode];
    if (!requiredScopes || !Array.isArray(requiredScopes)) return false;

    if (
      customerScopeOfBusiness.isLicenseExpired &&
      customerScopeOfBusiness.scopes?.includes('PRESCRIPTION') &&
      requiredScopes?.includes('PRESCRIPTION')
    ) {
      return true;
    }

    const hasRequiredScope = customerScopeOfBusiness.scopes.some((scope) => requiredScopes.includes(scope));
    return !hasRequiredScope;
  };

  /**
   * @deprecated LEGACY 2026-07-19 — GIỮ ĐỂ REFERENCE / AUDIT, KHÔNG DÙNG.
   * FE-only bridge cho license expiry check khi BFF chưa cover (TD-21 —
   * task #143). Theo nguyên tắc "FE chỉ đọc eligibility từ BFF", helper này
   * cũng thuộc diện eligibility computation → phải xoá khi task #143 landed.
   * Tách khỏi isConsultMedicine (chỉ giữ nhánh license expiry, bỏ scope-match
   * duplicate).
   *
   * @param {Object} productData - Product object (DTO hoặc raw)
   * @returns {boolean} True nếu license expired + prescription drug
   */
  // eslint-disable-next-line no-unused-vars
  const isLicenseExpiredForPrescription = (productData = {}) => {
    if (!configs || !productData?.sellerSubCategoryCode) return false;

    const customerValidationConfig = configs['CUSTOMER_LICENSE_VALIDATION_CONFIG'];
    if (!customerValidationConfig) return false;

    const requiredScopes = customerValidationConfig[productData.sellerSubCategoryCode];
    if (!Array.isArray(requiredScopes)) return false;

    return (
      customerScopeOfBusiness.isLicenseExpired &&
      customerScopeOfBusiness.scopes?.includes('PRESCRIPTION') &&
      requiredScopes.includes('PRESCRIPTION')
    );
  };

  /**
   * Normalize the child SKU list of a combo product across the two data shapes:
   * - Converted DTO (grid/detail): children live on `product.skus`
   * - Raw product data (cart/detail source): children live on `product.productData?.sku?.skus`
   *
   * @param {Object} product - Product object (DTO or raw)
   * @returns {Array} Array of child SKU items (empty when not a combo or no children)
   */
  const getComboChildren = (product = {}) => {
    const isCombo = product?.isCombo || product?.productData?.product?.isCombo;
    if (!isCombo) return [];
    return product?.skus || product?.productData?.sku?.skus || [];
  };

  /**
   * Extract the sellerCategoryCode of every combo child, tolerating both shapes:
   * - Already-converted child: `child.sellerCategoryCode`
   * - Raw child: `child.productData?.product?.sellerCategoryCode`
   *
   * @param {Object} product - Combo product object
   * @returns {string[]} List of child seller category codes
   */
  const getComboChildCategoryCodes = (product = {}) =>
    getComboChildren(product)
      .map((child) => child?.sellerCategoryCode || child?.productData?.product?.sellerCategoryCode)
      .filter(Boolean);

  /**
   * @deprecated LEGACY 2026-07-19 — GIỮ ĐỂ REFERENCE / AUDIT, KHÔNG DÙNG.
   * Client-side BV-1120 whitelist check (duplicate BFF
   * computeBusinessScopeEligibility). Theo nguyên tắc "FE chỉ đọc eligibility
   * từ BFF", check này gây drift — đặc biệt lộ ra khi business decision
   * 2026-07-19 miễn gate cho SKU EXTERNAL (BFF trả cantBuy=false nhưng FE
   * whitelist vẫn block). Vẫn được export vì backward-compat với callers cũ;
   * public isProductBlockedForBusiness đã refactor tin BFF eligibility.
   *
   * @param {Object} product - Product object (DTO or raw)
   * @returns {boolean} True if the product is within the allowed categories
   */
  // eslint-disable-next-line no-unused-vars
  const isProductInAllowedCategories = (product = {}) => {
    const isCombo = product?.isCombo || product?.productData?.product?.isCombo;
    if (isCombo) {
      const children = getComboChildren(product);
      if (children.length === 0) return false;
      const codes = getComboChildCategoryCodes(product);
      // Any child missing its sellerCategoryCode (filtered out) → blocked
      if (codes.length !== children.length) return false;
      return codes.every((code) => MEDICAL_SUPPLY_ALLOWED_CATEGORY_CODES.includes(code));
    }
    const code = product?.sellerCategoryCode || product?.productData?.product?.sellerCategoryCode;
    return Boolean(code) && MEDICAL_SUPPLY_ALLOWED_CATEGORY_CODES.includes(code);
  };

  /**
   * Detect combo across the three product shapes: converted DTO (isCombo),
   * raw product data (productData.product.isCombo), cart line item (type COMBO).
   *
   * @param {Object} product - Product object
   * @returns {boolean} True if the product is a combo
   */
  const isComboProduct = (product = {}) =>
    Boolean(product?.isCombo || product?.productData?.product?.isCombo || `${product?.type || ''}`.toUpperCase() === 'COMBO');

  /**
   * @deprecated LEGACY 2026-07-19 — GIỮ ĐỂ REFERENCE / AUDIT, KHÔNG DÙNG.
   * Client-side MEDICAL_SUPPLY_STORE gate với FE whitelist + combo walk.
   * Duplicate BFF computeBusinessScopeEligibility (frontend-apis-v2/action/
   * eligibility.go). Theo nguyên tắc "FE chỉ đọc eligibility từ BFF", check
   * này gây drift — đặc biệt lộ ra khi business decision 2026-07-19 miễn
   * gate cho SKU EXTERNAL (BFF cantBuy=false nhưng FE whitelist vẫn block).
   *
   * Đã thay bằng isProductBlockedForBusiness minimal (chỉ đọc
   * eligibility.cantBuy). Callers hiện tại (cart page) dùng version mới qua
   * export cùng tên.
   *
   * Body dưới đây là pre-refactor logic từ prd (2026-07-19 base):
   * Precedence:
   *   1) BFF verdict — nếu product.eligibility CÓ MẶT thì tin THẲNG cantBuy (cả
   *      true lẫn false). BFF walk SKU con của combo server-side; FE KHÔNG được
   *      override verdict này bằng whitelist cục bộ. Combo cha thường rỗng mã,
   *      hoặc combo dạng DEAL không có cờ isCombo → nếu để FE tự chấm sẽ
   *      fail-closed nhầm dù BFF đã trả cantBuy=false. Reason dùng chung
   *      'OUT_OF_SCOPE_OF_BUSINESS' cho cả BV-1080 & BV-1120
   *      (frontend-apis-v2/action/eligibility.go:16,29) nên chỉ căn cantBuy.
   *   2) Không có eligibility từ BFF → FE tự quyết: combo resolve đủ mã con →
   *      whitelist từng con; combo thiếu data con → fail-closed; non-combo →
   *      whitelist trên mã của chính sản phẩm.
   *
   * @param {Object} product - Product object
   * @returns {boolean} True if the purchase is blocked for this business type
   */
  // eslint-disable-next-line no-unused-vars
  const isProductBlockedForBusinessLegacy = (product = {}) => {
    if (user?.scope !== ENUM_SCOPE.MEDICAL_SUPPLY_STORE) return false;

    const eligibility = product?.eligibility;
    // BFF-first: có verdict thì tin thẳng, không fallback whitelist FE.
    if (eligibility && typeof eligibility.cantBuy === 'boolean') {
      return eligibility.cantBuy === true;
    }

    if (isComboProduct(product)) {
      const children = getComboChildren(product);
      const codes = getComboChildCategoryCodes(product);
      if (children.length > 0 && codes.length === children.length) {
        return !codes.every((code) => MEDICAL_SUPPLY_ALLOWED_CATEGORY_CODES.includes(code));
      }
      // Combo không resolve được đủ mã con và cũng không có verdict BFF → fail-closed.
      return true;
    }

    return !isProductInAllowedCategories(product);
  };

  /**
   * Check if buying a product is blocked for BUSINESS_SCOPE_GATED_SCOPES customer.
   * Chỉ đọc BFF eligibility.cantBuy — BFF đã xử lý BV-1120 whitelist +
   * business decisions (như EXTERNAL exemption 2026-07-19) trong
   * computeBusinessScopeEligibility + skipGateForSKU.
   *
   * Với customer ngoài BUSINESS_SCOPE_GATED_SCOPES: return false (function này chỉ
   * quan tâm business-type gate).
   * Với BUSINESS_SCOPE_GATED_SCOPES + eligibility.cantBuy=true → blocked.
   * Với BUSINESS_SCOPE_GATED_SCOPES + eligibility vắng: fail-open (BFF chưa wire
   * eligibility cho endpoint đó — tasks P1 #97-100 pending).
   *
   * @param {Object} product - Product object (phải có product.eligibility từ BFF)
   * @returns {boolean} True if blocked for BUSINESS_SCOPE_GATED_SCOPES by BFF
   */
  const isProductBlockedForBusiness = (product = {}) => {
    if (!BUSINESS_SCOPE_GATED_SCOPES.includes(user?.scope)) return false;
    return product?.eligibility?.cantBuy === true;
  };

  /**
   * Show ConsultBtn khi BFF nói không được mua HOẶC product là special
   * ingredient (regulatory consult trigger — không phụ thuộc business scope).
   *
   * FE KHÔNG compute eligibility — chỉ đọc BFF verdict. Nếu customer expired
   * license / thiếu scope / vi phạm BV-1120 → BFF sẽ trả cantBuy=true.
   *
   * @param {Object} product - Product object
   * @param {Object} [product.eligibility] - BFF eligibility payload (cantBuy, reason, message)
   * @param {boolean} [product.isIngredientSpecial] - Regulatory product attribute
   * @returns {boolean} True if ConsultBtn should be shown
   */
  const shouldShowConsultButton = (product = {}) => {
    if (product?.eligibility?.cantBuy === true) return true;
    return Boolean(product?.isIngredientSpecial);
  };
  const shouldDisableConsultButton = (product = {}) =>
    Boolean(product?.isIngredientSpecial && isCustomerInBlockedProvince);

  return {
    isProductInAllowedCategories,
    isProductBlockedForBusiness,
    shouldShowConsultButton,
    shouldDisableConsultButton,
  };
}

